import { coreApi } from '@shared/api/coreApi';
import { tenantApi } from '@shared/api/tenantApi';
import { getJoinTenantContext } from '@shared/api/joinTenantContext';
import { unwrap } from '@shared/api/unwrap';
import { useAuthStore } from '@shared/stores/authStore';
import { ssoSessionApi } from '@shared/api/ssoSessionApi';
import { env } from '@config/env';

/**
 * Join-a-tenant + onboarding-gate API — mirrors the Angular member-portal
 * `JoinTenantService` and the onboarding gate the `OnboardingGuard` reads.
 *
 * SSO + tenant switching itself lives in `features/auth/api.ts`
 * (`/portal-auth/login`, `/portal-auth/switch-tenant`,
 * `/portal-auth/joinable-tenants`) — this module covers the *join* journey:
 * discover a community, request to join, then clear the onboarding gate.
 */

export interface TenantSearchResult {
  tenant_id: string;
  name: string;
  /** Member-portal hostname for Core resolve-domain + Tenant API signup. */
  resolution_domain: string | null;
  logo_small_url?: string | null;
  logo_large_url?: string | null;
}

export interface JoinRequestMemberData {
  first_name?: string;
  last_name?: string;
  email?: string;
  member_type_id?: string;
  /** Terms documents the member ticked, by id — validated against the target community's own set. */
  accepted_term_ids?: string[];
  id_number?: string;
  membership_number?: string;
}

export interface JoinRequestResult {
  member_request_id: string;
  onboarding_id?: string;
  flow_instance_id?: string;
  /** Sent with `flow_instance_id`: the runner needs it on every call on that instance. */
  flow_instance_token?: string;
}

export interface JoinStatus {
  member_request_id: string;
  tenant_id: string;
  request_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * The shape returned by both `GET /members/me/onboarding-gate` (post-login)
 * and `GET /public/member-requests/join/onboarding-gate` (join flow).
 */
export interface OnboardingGate {
  requires_onboarding: boolean;
  onboarding_status?: string | null;
  flow_instance_id?: string | null;
  /** Sent with `flow_instance_id` while there is still something to fill in. */
  flow_instance_token?: string;
  onboarding_id?: string | null;
  flow_template_id?: string | null;
}

/** The runner link for an instance handed out with its token. */
export function flowInstanceRoute(instanceId: string, token: string | undefined): string {
  return `/onboarding?instance=${encodeURIComponent(instanceId)}&token=${encodeURIComponent(token ?? '')}`;
}

/**
 * Search joinable tenants within a root scope (Core API, public).
 *
 * `coreUserId` drops the communities this member already belongs to, so the list only
 * offers ones they can actually join — Core answers it from its own membership records,
 * which cover links made outside SSO too.
 */
export async function searchTenantsByRoot(
  rootTenantId: string,
  query?: string,
  coreUserId?: string,
): Promise<TenantSearchResult[]> {
  const { data } = await coreApi.get<{ tenants?: TenantSearchResult[] }>(
    '/tenants/search-by-root',
    {
      params: {
        root_tenant_id: rootTenantId,
        q: query?.trim() || undefined,
        core_user_id: coreUserId || undefined,
      },
    },
  );
  const tenants = data.tenants ?? [];

  // Core never returns the root itself, so a build that lists it adds it here: matched by the
  // same search term, and left out of the join list once the member already belongs to it.
  const root = env.rootCommunity;
  const term = query?.trim().toLowerCase();
  const matchesTerm = !term || root?.name.toLowerCase().includes(term);
  const alreadyMember = !!coreUserId
    && useAuthStore.getState().linkedTenants.some((t) => t.tenant_id === rootTenantId);

  if (!root || !env.defaultDomain || !matchesTerm || alreadyMember) return tenants;

  return [{ tenant_id: rootTenantId, name: root.name, resolution_domain: env.defaultDomain }, ...tenants];
}

/**
 * The target community's domain-config id — the handle its terms documents hang off.
 * Fetched rather than read from `moduleStore`, whose config belongs to the community the
 * session is currently in, not the one being joined.
 */
export async function fetchJoinDomainConfigId(domain: string): Promise<string | undefined> {
  const { data } = await tenantApi.get('/public/domain-config', { params: { domain } });
  return unwrap<{ domainConfigId?: string }>(data)?.domainConfigId;
}

/**
 * A join call, signed with the Core SSO session token. The member has no account in the
 * community being joined, so its API takes who is asking from Core's signature, never from
 * the request.
 */
async function joinSessionRequest<T>(
  method: 'get' | 'post',
  url: string,
  options: { data?: unknown; tenantId?: string } = {},
): Promise<T> {
  const auth = useAuthStore.getState();
  if (!auth.ssoSessionToken) throw new Error('Please sign in again to join a community.');

  const { data } = await ssoSessionApi.request({
    method,
    url,
    data: options.data,
    baseURL: auth.regionalApiUrl || env.apiBaseUrl,
    headers: { 'x-tenant-id': options.tenantId ?? getJoinTenantContext() ?? auth.tenantId },
  });
  return unwrap<T>(data);
}

/** Submit the signed-in member's request to join a tenant. */
export async function joinTenant(
  memberData?: JoinRequestMemberData,
  rootTenantId?: string,
): Promise<JoinRequestResult> {
  return joinSessionRequest<JoinRequestResult>('post', '/public/member-requests/join', {
    data: {
      ...(memberData && Object.keys(memberData).length ? { member_data: memberData } : {}),
      ...(rootTenantId ? { root_tenant_id: rootTenantId } : {}),
    },
  });
}

/** The signed-in member's latest join request on the tenant being joined. */
export async function fetchJoinStatus(): Promise<JoinStatus | null> {
  return joinSessionRequest<JoinStatus | null>('get', '/public/member-requests/join/status');
}

/**
 * Onboarding gate for a pending join request (pre tenant-context switch).
 *
 * `tenantId` addresses a community the app is not currently inside — the drawer's pending
 * entry asks about one while the session sits in another, and the ambient tenant would
 * answer for the wrong community.
 */
export async function fetchJoinOnboardingGate(tenantId?: string): Promise<OnboardingGate> {
  return joinSessionRequest<OnboardingGate>('get', '/public/member-requests/join/onboarding-gate', {
    tenantId,
  });
}

/**
 * Post-login onboarding gate for the authenticated member. The
 * `OnboardingGuard` equivalent: if `requires_onboarding`, route the member
 * into `/onboarding` with the supplied flow/instance ids before letting
 * them into the app.
 */
export async function fetchMyOnboardingGate(): Promise<OnboardingGate> {
  const { data } = await tenantApi.get('/members/me/onboarding-gate');
  return unwrap<OnboardingGate>(data);
}

/**
 * The member has finished the flow and is waiting on the estate office. The
 * gate still withholds app access (approval is staff-driven), but there is
 * nothing left for them to fill in — sending them back into the runner would
 * just bounce them between a completed flow and the gate.
 */
export function isAwaitingOnboardingApproval(gate: OnboardingGate): boolean {
  return (
    gate.requires_onboarding &&
    (gate.onboarding_status === 'submitted' || gate.onboarding_status === 'under_review')
  );
}

/** Build the onboarding route a gate result should redirect to. */
export function onboardingRouteForGate(gate: OnboardingGate): string | null {
  if (!gate.requires_onboarding) return null;
  if (gate.flow_instance_id) return flowInstanceRoute(gate.flow_instance_id, gate.flow_instance_token);
  if (gate.flow_template_id) {
    const onboardingParam = gate.onboarding_id ? `&onboardingId=${gate.onboarding_id}` : '';
    return `/onboarding/${gate.flow_template_id}?${onboardingParam.slice(1)}`;
  }
  return '/onboarding';
}
